<template>
  <div class="nav-wrapper" :class="{ 'scrolled': isScrolled }">
    <!-- Logo - add mobile-visible class -->
    <router-link to="/" class="logo" :class="{ 'mobile-visible': isMobile }">
      <img v-if="logoUrl" :src="logoUrl" alt="360° Collective" class="logo-image">
      <span v-else>360°</span>
    </router-link>

    <!-- Navigation Toggle -->
    <div class="nav-toggle" id="navToggle" @click="toggleNav" :class="{ active: isNavActive }">
      <div class="square-grid">
        <span class="square"></span>
        <span class="square"></span>
        <span class="square"></span>
      </div>
    </div>

    <!-- Navigation Menu -->
    <div class="nav-background" :class="{ active: isNavActive }"></div>
    <nav class="navigation" :class="{ active: isNavActive }">
      <ul class="nav-links">
        <li style="--index: 1;"><router-link to="/" @click="closeNav">Home</router-link></li>
        <li style="--index: 2;"><a href="#about" @click.prevent="scrollTo('about')">About</a></li>
        <li style="--index: 2;"><a href="/workflow">Workflow</a></li>
        
        <li style="--index: 5;"><a href="#members" @click.prevent="scrollTo('members')">Members</a></li>
        <li style="--index: 4;"><a href="#projects" @click.prevent="scrollTo('projects')">Projects</a></li>
        
        <li style="--index: 6;"><router-link to="/contact" @click="closeNav">Contact</router-link></li>
      </ul>
    </nav>
  </div>
</template>

<script>
import mediaService from '@/services/mediaService';

export default {
  name: 'TheNavigation',
  data() {
    return {
      isNavActive: false,
      logoUrl: null,
      mobileMenuOpen: false,
      scrollPosition: 0,
      scrollToSection: null,
      isScrolled: false,
      heroHeight: 0,
      isMobile: false,
      // Define scroll thresholds for different screen sizes
      scrollThresholds: {
        mobile: 50,    // Smaller threshold for mobile
        tablet: 450,    // Medium threshold for tablet
        desktop: 600    // Larger threshold for desktop
      }
    }
  },
  methods: {
    toggleNav() {
      if (!this.isNavActive) {
        // Save current scroll position before opening menu
        this.scrollPosition = window.pageYOffset;
        this.isNavActive = true;
        document.body.classList.add('nav-open');
        document.body.style.top = `-${this.scrollPosition}px`;
        document.body.style.overflow = 'hidden';
        
        // Ensure navigation is visible before animation starts
        setTimeout(() => {
          const navElement = document.querySelector('.navigation');
          if (navElement) navElement.style.visibility = 'visible';
        }, 10);
      } else {
        // Close the menu and restore scroll position
        this.isNavActive = false;
        
        // Add a timeout to wait for the closing animation
        setTimeout(() => {
          document.body.classList.remove('nav-open');
          document.body.style.overflow = 'auto';
          document.body.style.top = '';
          window.scrollTo(0, this.scrollPosition);
        }, 1000); // Match this with the animation duration
      }
    },
    closeNav() {
      this.isNavActive = false;
      
      // Add a timeout to wait for the closing animation
      setTimeout(() => {
        document.body.classList.remove('nav-open');
        document.body.style.overflow = 'auto';
        document.body.style.top = '';
        
        // Only restore scroll position if we're not scrolling to a section
        if (!this.scrollToSection && this.scrollPosition) {
          window.scrollTo(0, this.scrollPosition);
        }
        
        // Reset the flag
        this.scrollToSection = null;
      }, 1000);
    },
    scrollTo(sectionId) {
      // Set flag to indicate we're scrolling to a section
      this.scrollToSection = sectionId;
      
      // Just set isNavActive to false to start closing animation
      this.isNavActive = false;
      
      // Check if we're already on the homepage
      const isHomePage = this.$route.path === '/' || this.$route.path === '/home';
      
      // Handle based on current page
      if (isHomePage) {
        // Already on homepage, just scroll after menu closes
        this.scrollToElementAfterDelay(sectionId);
      } else {
        // Need to navigate to homepage first, then scroll
        setTimeout(() => {
          document.body.classList.remove('nav-open');
          document.body.style.overflow = 'auto';
          document.body.style.top = '';
          
          // Navigate to homepage with section as hash
          this.$router.push({ 
            path: '/', 
            hash: `#${sectionId}` 
          }).then(() => {
            // After navigation is complete, try to scroll to element
            // Add a slight delay to ensure the page has loaded
            setTimeout(() => {
              this.scrollToElement(sectionId);
            }, 500);
          }).catch(err => {
            console.error('Navigation error:', err);
          });
        }, 400);
      }
    },
    scrollToElementAfterDelay(sectionId) {
      // Handle scrolling after the menu closing animation completes
      setTimeout(() => {
        document.body.classList.remove('nav-open');
        document.body.style.overflow = 'auto';
        document.body.style.top = '';
        
        this.scrollToElement(sectionId);
      }, 400);
    },
    scrollToElement(sectionId) {
      // Find and scroll to the element
      const element = document.getElementById(sectionId);
      if (element) {
        // Scroll with smooth behavior
        element.scrollIntoView({ behavior: 'smooth' });
        
        // For Safari compatibility, backup scrolling
        if (typeof window.safari !== 'undefined') {
          window.scrollTo({
            top: element.offsetTop,
            behavior: 'smooth'
          });
        }
      } else {
        console.warn(`Element with id ${sectionId} not found`);
      }
    },
    toggleMobileMenu() {
      this.mobileMenuOpen = !this.mobileMenuOpen;
    },
    async fetchLogo() {
      try {
        const logoId = '876eaf7c48f72188e72f';
        console.log('Fetching logo with ID:', logoId);
        
        const logoUrl = await mediaService.getMediaUrl(logoId, 'webp_medium');
        
        if (logoUrl) {
          this.logoUrl = logoUrl;
          console.log('Successfully loaded logo:', this.logoUrl);
        }
      } catch (error) {
        console.error('Error fetching logo:', error);
      }
    },
    handleScroll() {
      const scrollPosition = window.scrollY;
      let threshold;
      
      // Determine which threshold to use based on screen width
      if (window.innerWidth < 768) {
        threshold = this.scrollThresholds.mobile;
      } else if (window.innerWidth < 1024) {
        threshold = this.scrollThresholds.tablet;
      } else {
        threshold = this.scrollThresholds.desktop;
      }
      
      this.isScrolled = scrollPosition > threshold;
    },
    updateHeroHeight() {
      // Find the hero section - adjust the selector if it has a different class or ID
      const heroSection = document.querySelector('.hero-section');
      if (heroSection) {
        // Don't subtract anything to make the background appear right when scrolling past hero
        this.heroHeight = heroSection.offsetHeight;
      } else {
        // Default value if hero section isn't found - increase to avoid early trigger
        this.heroHeight = 700; // Larger default value
      }
    },
    updateDeviceSize() {
      // Update isMobile flag based on screen width
      this.isMobile = window.innerWidth < 768;
    }
  },
  created() {
    this.fetchLogo();
  },
  mounted() {
    // Add scroll event listener
    window.addEventListener('scroll', this.handleScroll);
    
    // Get initial hero section height
    this.$nextTick(() => {
      this.updateHeroHeight();
    });
    
    // Check initial device size
    this.updateDeviceSize();
    
    // Also listen for window resize to update measurements
    window.addEventListener('resize', this.updateHeroHeight);
    window.addEventListener('resize', this.updateDeviceSize);
    
    // Check for hash in URL when component mounts
    if (this.$route.hash) {
      // If there's a hash, try to scroll to that element
      const sectionId = this.$route.hash.substring(1); // Remove the # symbol
      setTimeout(() => {
        this.scrollToElement(sectionId);
      }, 500);
    }
  },
  beforeUnmount() {
    // Clean up event listeners
    window.removeEventListener('scroll', this.handleScroll);
    window.removeEventListener('resize', this.updateHeroHeight);
    window.removeEventListener('resize', this.updateDeviceSize);
  }
}
</script>

<style scoped>
/* Navigation Wrapper */
.nav-wrapper {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  z-index: 100;
  padding: 2rem;
  pointer-events: none;
  transition: all 0.3s cubic-bezier(0.25, 0.46, 0.45, 0.94);
  /* Add height to ensure background covers the entire header area */
  height: auto;
}

/* Scrolled state styling */
.nav-wrapper.scrolled {
  background-color: var(--white);
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
  padding: 1rem 2rem;
  /* Explicit height for the header when scrolled */
  height: 5rem; /* This creates the white bar with proper height */
}

.nav-wrapper.scrolled .square {
  background-color: var(--black);
}

.nav-wrapper.scrolled .logo {
  color: var(--black);
}

/* Logo */
.logo {
  position: fixed;
  top: 2rem;
  left: 2rem;
  z-index: 999;
  font-family: 'Syne', sans-serif;
  font-weight: 800;
  font-size: 1.5rem;
  color: var(--black);
  display: flex;
  align-items: center;
  pointer-events: auto;
  transition: all 0.3s cubic-bezier(0.25, 0.46, 0.45, 0.94);
  
  /* Hide logo by default for tablet/desktop (will show when scrolled) */
  opacity: 0;
  visibility: hidden;
}

/* Show logo when scrolled for all devices */
.nav-wrapper.scrolled .logo {
  opacity: 1;
  visibility: visible;
  top: 1rem;
}

/* Always show logo on mobile */
.logo.mobile-visible {
  opacity: 0;
  visibility: visible;
}

.logo-image {
  height: 2.5rem;
  width: auto;
}

/* Navigation Toggle */
.nav-toggle {
  position: fixed;
  top: 2rem;
  right: 2rem;
  z-index: 1000;
  width: 3rem;
  height: 3rem;
  background: none;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  border: none;
  /*border: 2px solid var(--accent);*/
  pointer-events: auto;
  transition: all 0.3s cubic-bezier(0.25, 0.46, 0.45, 0.94);
}

/* Apply transitioning to the nav toggle position when scrolled */
.nav-wrapper.scrolled .nav-toggle {
  top: 1rem;
}

/* Replace the hamburger span styling with square grid */
.square-grid {
  display: grid;
  grid-template-columns: 1fr;
  grid-template-rows: 1fr 1fr 1fr;
  gap: 10px;
  width: 80%;
  height: 60%;
}

.square {
  background-color: var(--accent);
  transition: all 0.4s cubic-bezier(0.25, 0.46, 0.45, 0.94);
}

/* The rest of styles remain unchanged */
.nav-toggle.active {
  background: none;
  border: 3px solid var(--black);
}

.nav-toggle.active .square {
  background-color: var(--black);
}

.nav-toggle.active .square:nth-child(1) {
  transform: scale(0.9);
}

.nav-toggle.active .square:nth-child(2) {
  transform: scale(0.9);
}

.nav-toggle.active .square:nth-child(3) {
  transform: scale(0.9);
}

.nav-toggle.active .square:nth-child(4) {
  transform: scale(0.9);
}

/* Navigation Menu */
.navigation {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100vh;
  background-color:rgb(251, 249, 246) !important;
  z-index: 997;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  transform: translateY(-100%);
  transition: transform 1s cubic-bezier(0.16, 1, 0.3, 1);
  pointer-events: auto;
  visibility: visible;
  opacity: 0;
}

.navigation.active {
  transform: translateY(0);
  opacity: 1;
}

.nav-links {
  list-style: none;
  padding: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
}

.nav-links li {
  margin: 1.5rem 0;
  overflow: hidden;
}

.nav-links a {
  display: inline-block;
  font-family: Avenir, Helvetica, Arial, sans-serif;
  font-size: 3rem;
  font-weight: 500;
  line-height: 1;
  transform: translateY(100%);
  transition: transform 1s cubic-bezier(0.16, 1, 0.3, 1), color 0.1s;
  letter-spacing: 0.02em;
  text-transform: uppercase;
  color: var(--black);
  opacity: 0;
  will-change: transform, opacity;
}

.navigation.active .nav-links a {
  transform: translateY(0);
  opacity: 1;
  transition: 0.2s;
}

.nav-links a:hover {
  color: var(--accent);
}

/* Fix for navigation toggle animation */
.nav-toggle span,
.nav-toggle span::before,
.nav-toggle span::after {
  will-change: transform, opacity;
}

/* Add styling for body when nav is open */
:global(body.nav-open) {
  overflow: hidden;
  height: 100vh;
}

/* Navigation Background Overlay */
.nav-background {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100vh;
  background-color:rgb(251, 249, 246) !important;
  z-index: 995;
  opacity: 0;
  pointer-events: none;
  transition: opacity 1s cubic-bezier(0.16, 1, 0.3, 1);
}

.nav-background.active {
  opacity: 1;
  pointer-events: auto;
}
</style> 